import { z } from "zod";

/**
 * Areas, dining tables and their QR codes. See docs/architecture/data-model.md,
 * Tables and guests, and storefront-sites.md, QR codes.
 */

/** 128 random bits in base62: 22 letters and digits. */
export const QR_TOKEN_LENGTH = 22;
export const QR_TOKEN_PATTERN = /^[0-9A-Za-z]{22}$/;

/** The storefront path a table's QR code opens: `/t/<qrToken>`. */
export function tableQrPath(qrToken: string): string {
	return `/t/${qrToken}`;
}

export const areaSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	sortOrder: z.number().int(),
});
export type Area = z.infer<typeof areaSchema>;

export const diningTableSchema = z.object({
	id: z.uuid(),
	/** Null: in no area. */
	areaId: z.uuid().nullable(),
	label: z.string(),
	seats: z.number().int().nullable(),
	/** False: not in use; its code shows the invalid-code page. */
	isActive: z.boolean(),
	sortOrder: z.number().int(),
	/** What the QR code holds: `https://<slug>.<root>/t/<qrToken>`. */
	qrUrl: z.url(),
});
export type DiningTable = z.infer<typeof diningTableSchema>;

/** `staff.tables.list`: areas by `sortOrder`, tables by `sortOrder` (creation order). */
export const tablesListSchema = z.object({
	areas: z.array(areaSchema),
	tables: z.array(diningTableSchema),
});
export type TablesList = z.infer<typeof tablesListSchema>;

/** Create with a client-made `id`; the same call again returns the same area. */
export const upsertAreaInputSchema = z.object({
	id: z.uuid(),
	name: z.string().trim().min(1).max(40),
});
export type UpsertAreaInput = z.input<typeof upsertAreaInputSchema>;

/**
 * Create with a client-made `id`; the same call again returns the same table.
 * Fields left out keep their value. A new table gets its QR token here.
 */
export const upsertTableInputSchema = z.object({
	id: z.uuid(),
	label: z.string().trim().min(1).max(20),
	areaId: z.uuid().nullable().optional(),
	seats: z.number().int().min(1).max(100).nullable().optional(),
	isActive: z.boolean().optional(),
});
export type UpsertTableInput = z.input<typeof upsertTableInputSchema>;

export const tableIdInputSchema = z.object({ tableId: z.uuid() });

/** `public.tenancy.resolveTable`. Any string: a malformed token is simply not found. */
export const resolveTableInputSchema = z.object({ qrToken: z.string().max(64) });

/** The table a QR code opens, in the restaurant of the host. */
export const resolvedTableSchema = z.object({
	restaurantId: z.uuid(),
	table: z.object({
		id: z.uuid(),
		label: z.string(),
		areaName: z.string().nullable(),
	}),
});
export type ResolvedTable = z.infer<typeof resolvedTableSchema>;

export const QR_FORMATS = ["png", "svg"] as const;

/** `staff.tables.getQrCode`: one table's code to view or download. */
export const qrCodeInputSchema = z.object({
	tableId: z.uuid(),
	format: z.enum(QR_FORMATS),
});

/** `staff.tables.getQrSheet`: these tables in list order, or every active table. */
export const qrSheetInputSchema = z.object({
	tableIds: z.array(z.uuid()).min(1).max(500).optional(),
});

/**
 * A generated file. Web: a Blob from the bytes, or a `data:` URL to show it.
 * Android: write the base64 to a file and open the share sheet.
 */
export const generatedFileSchema = z.object({
	fileName: z.string(),
	mime: z.string(),
	base64: z.string(),
});
export type GeneratedFile = z.infer<typeof generatedFileSchema>;
